import { useEffect, useRef, useState } from 'react';
import { MAX_SCENE_TEXT_CHARS } from '@scoutaround/script-breakdown-shared';

/**
 * ADD A SCENE THE PARSER MISSED — with its text (owner request, 2026-10-29).
 *
 * A scene added with a heading alone could never be analysed: the AI breakdown
 * reads a scene's text, and there was none. The text box sits right under the
 * heading, says what it is for, and is optional — a producer who only knows
 * there is a scene here can still add it, and the scene list will say it has no
 * text yet.
 *
 * What is typed is stored as the scene's own text and labelled as typed on every
 * screen. The uploaded screenplay file is never edited.
 */
export function AddSceneForm({
  title,
  busy,
  error,
  inline = false,
  onCancel,
  onSubmit,
}: {
  /** Where the scene goes, in words: "A scene after 12 — INT. KITCHEN - NIGHT". */
  title: string;
  busy: boolean;
  error: string | null;
  /** Drawn in a row under its scene rather than above the table. */
  inline?: boolean;
  onCancel: () => void;
  onSubmit: (input: { slugline: string; text: string }) => void;
}) {
  const [slugline, setSlugline] = useState('');
  const [text, setText] = useState('');
  const panel = useRef<HTMLDivElement | null>(null);
  const tooLong = text.length > MAX_SCENE_TEXT_CHARS;

  // Where the press was: scroll only if the form would open off-screen, and
  // put the keyboard in the heading field.
  useEffect(() => {
    const node = panel.current;
    if (node === null) return;

    node.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    node.querySelector<HTMLInputElement>('input')?.focus();
  }, []);

  return (
    <div
      ref={panel}
      role="dialog"
      aria-labelledby="add-scene-title"
      data-testid="add-scene-dialog"
      className="scene-form"
      data-inline={inline ? '1' : '0'}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !busy) onCancel();
      }}
    >
      <p id="add-scene-title" className="scene-form-title">
        {title}
      </p>

      <label className="scene-field">
        <span className="scene-field-label">Scene heading</span>
        <input
          className="intake-input scene-mono"
          data-testid="add-scene-slugline"
          value={slugline}
          placeholder="INT. HOTEL CORRIDOR - NIGHT"
          onChange={(e) => {
            setSlugline(e.target.value);
          }}
        />
      </label>

      <label className="scene-field">
        <span className="scene-field-label">
          Scene text <span className="mm">(optional)</span>
        </span>
        <textarea
          className="intake-textarea scene-mono"
          data-testid="add-scene-text"
          rows={6}
          value={text}
          placeholder="What happens in this scene — action, characters, dialogue. The AI breakdown reads this text."
          onChange={(e) => {
            setText(e.target.value);
          }}
        />
        <span className="scene-field-hint" data-testid="add-scene-text-hint">
          {tooLong
            ? `Too long by ${(text.length - MAX_SCENE_TEXT_CHARS).toLocaleString('en-GB')} characters.`
            : 'Added to the breakdown as typed text — the uploaded screenplay file is not changed.'}
        </span>
      </label>

      {error === null ? null : (
        <p data-testid="add-scene-error" className="intake-error" role="alert">
          {error}
        </p>
      )}

      <div className="scene-form-actions">
        <button type="button" className="ctl" data-testid="add-scene-cancel" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="btn"
          data-testid="add-scene-submit"
          disabled={slugline.trim() === '' || tooLong || busy}
          onClick={() => {
            onSubmit({ slugline: slugline.trim(), text });
          }}
        >
          {busy ? 'Adding…' : 'Add scene'}
        </button>
      </div>
    </div>
  );
}
